import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import "react-tabulator/lib/styles.css";
import "react-tabulator/css/bootstrap/tabulator_bootstrap.min.css";
import {
  ColumnDefinition,
  ReactTabulator,
  ReactTabulatorOptions,
} from "react-tabulator";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ReportRequest,
  LeadsService,
  ReportTypes,
  LeadsUserReport,
  LeadsDateReport,
  ClioService,
  UsersModel,
  ApplicationRole,
  OpenAPI,
  ReportModel,
} from "../../api";
import { DatePicker } from "@mui/x-date-pickers";
import { TabsHelpers } from "../../components/basic/TabsHelpers";
import {
  AllSelection,
  Presets,
  ReportDetails,
  ReportSource,
  ReportsData,
} from "./ReportsOptions";
import { HelpersService } from "../../services/Helpers";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { startSpinner, stopSpinner } from "../../redux/actions/spinnerActions";
import { DateTime } from "luxon";
import "./Reports.scss";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useParams } from "react-router-dom";
import { CustomFieldOption } from "../../api/models/CustomField";
import { MatterData } from "../../api/models/MatterData";
import { LitigationReportRequest } from "../../api/models/LitigationReportRequest";
import { LitigationReportTable, LitigationReportTableHandle } from "./LitigationFunnel/LitigationReportTable";
import { string } from "yup";

const MySwal = withReactContent(Swal);

interface ReportsProps {
  source: ReportSource;
}

export default function Reports(props: ReportsProps) {
  const [tab, setTab] = useState<number>(0);
  const [tableData, setTableData] = useState<
    LeadsUserReport[] | LeadsDateReport[] | ReportModel[]
  >([]);
  const [filteredTableData, setFilteredTableData] = useState<
    LeadsUserReport[] | LeadsDateReport[] | ReportModel[]
  >([]);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [selectedPreset, setSelectedPreset] = useState<any>("Month");
  const [selectedReportData, setSelectedReportData] = useState<ReportDetails>();
  const [usersList, setUsersList] = useState<UsersModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null | undefined>(
    null
  );
  const [selectedAssignee, setSelectedAssignee] = useState<string | null | undefined>(
    null
  );
  const [selectedReport, setSelectedReport] = useState<ReportTypes>();

  // Litigation Funnel Report
  const [matterOptions, setMatterOptions] = useState<MatterData[]>([]);
  const [assignedParalegals, setAssignedParalegals] = useState<CustomFieldOption[]>([]);
  const [responsibleAttorneys, setResponsibleAttorneys] = useState<CustomFieldOption[]>([]);
  const [seniorManagingAttorneys, setSeniorManagingAttornyes] = useState<CustomFieldOption[]>([]);
  const [supportingAttorneys, setSupportingAttorneys] = useState<CustomFieldOption[]>([]);
  const [notesDirty, setNotesDirty] = useState(false);

  const [reportFilter, setReportFilter] = useState<LitigationReportRequest>({})
  const [search, setSearch] = useState<string>('')
  
  let table;
  const dispatch = useDispatch();
  const { reportType } = useParams();

  const zoomPhoneKey = "ZOOM_EXT_ID";
  const phoneId = localStorage.getItem(zoomPhoneKey);
  const user = useSelector((state: any) => state.user?.user);
  const userAdminRoles = [
    ApplicationRole.ADMIN,
    ApplicationRole.CLIO_ADMIN,
    ApplicationRole.CLIO_MANAGER,
  ];

  const litigationTableRef = useRef<LitigationReportTableHandle | null>(null);

  // @ts-ignore
  if (!window.DateTime) {
    // @ts-ignore
    window.DateTime = DateTime;
  }
  // @ts-ignore
  if (!window.luxon) {
    // @ts-ignore
    window.luxon = DateTime;
  }

  useEffect(() => {
    ClioService.getApiClioClioUsers().then((users) => {
      setUsersList(users);
    });
    
    ClioService.getApiClioLitigationMatters(true).then((matters) => {
        setMatterOptions(matters.sort((a, b) => {
            const aName = `${a.display_number}`;
            const bName = `${b.display_number}`;
            return aName.localeCompare(bName)
        }));
    });

    loadCustomFieldOptions("Assigned Paralegal (AP)", setAssignedParalegals);
    loadCustomFieldOptions("Senior Managing Atty (SMA)", setSeniorManagingAttornyes);
    loadCustomFieldOptions("Supporting Atty (SA)", setSupportingAttorneys);
    loadCustomFieldOptions("Responsible Associate Atty (RAA)", setResponsibleAttorneys);
  }, [props.source]);

  const loadCustomFieldOptions = async (
      fieldName: string,
      setter: (options: CustomFieldOption[]) => void
      ): Promise<void> =>  {
      try {
          const customFields = await ClioService.getApiClioCustomFields(fieldName);
          const options = customFields.find(c => c.name === fieldName)?.picklist_options ?? [];
          setter(options.sort((a, b) => a.option.localeCompare(b.option)));
      } catch (error) {
          console.error(`Failed to load options for ${fieldName}`, error);
          setter([]);
      }
  }

  useEffect(() => {
    if (!reportType) {
      return;
    }
    setSelectedReport(reportType as ReportTypes);
  }, [reportType]);

  useEffect(() => {
    setTableData([]);
    if (!selectedReport) {
      return;
    }
    setSelectedPreset("Month");
    if(selectedReport === ReportTypes.SOLES || selectedReport === ReportTypes.TASKS || selectedReport === ReportTypes.LITIGATION_FUNNEL){
      setStartDate(null);
      setEndDate(null);
    }
    else if (
      selectedReport === ReportTypes.HOURS ||
      selectedReport === ReportTypes.MATTER_STAGE_CHANGE
    ) {
      setStartDate(dayjs().startOf("month"));
      setEndDate(dayjs(new Date()));
    } else {
      clearData();
    }
    setSelectedReportData(
      ReportsData.find((data) => data.type === selectedReport)
    );
  }, [selectedReport]);

  const clearData = () => {
    setEndDate(
      reportType == ReportTypes.SETTLEMENT_LIT || reportType == ReportTypes.SETTLEMENT_PRELIT || reportType === ReportTypes.LITIGATION_FUNNEL
        ? null
        : dayjs(new Date())
    );
    setStartDate(null);
    setSelectedPreset(AllSelection);
    setSelectedUser(null);
    setSelectedAssignee(null);
  };

  useEffect(() => {
    if (selectedReport === ReportTypes.LITIGATION_FUNNEL) {
      const loweredSearch = search.toLocaleLowerCase();
      setFilteredTableData(tableData.map((tb) => ({
        ...tb,
        reportLevel2: (tb as ReportModel).reportLevel2?.filter(r2 => loweredSearch === '' || r2.level2Data5?.toLowerCase().includes(loweredSearch))
      })).filter(tb => ( (tb as ReportModel)?.reportLevel2?.length ?? 0) > 0));
    } else {
      setFilteredTableData(tableData)
    }
  }, [tableData, search])

  useEffect(() => {
    if (selectedReport !== ReportTypes.LITIGATION_FUNNEL) return;

    const allFields =
      (selectedReportData?.columns ?? [])
        .slice(2)
        .map((c: any) => c?.field)
        .filter((f: any): f is string => typeof f === "string" && f.length > 0)
        .map((f) => f.toLowerCase());

    setReportFilter((prev) => ({
      ...prev,
      downloadColumns: allFields,
    }));
  }, [selectedReport, selectedReportData]);


  const handleGenerate = async () => {
    table.current.download("csv", "export.csv", {});
  };

  const updatePropertyNames = (data) => {
    return data.map((item) => {
      const updatedItem = { ...item };

      //user report
      if (updatedItem.leadsByStatus) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.leadsByStatus
        );
        delete updatedItem.leadsByStatus;
      }
      if (updatedItem.leadsByCategory) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.leadsByCategory
        );
        delete updatedItem.leadsByCategory;
      }

      if (updatedItem.leads) {
        updatedItem["_children"] = updatePropertyNames(updatedItem.leads);
        delete updatedItem.leads;
      }

      if (updatedItem.createdAt) {
        updatedItem.createdAt = new Date(
          updatedItem.createdAt
        ).toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });
      }
      if (updatedItem.completedAt) {
        updatedItem.completedAt =
          updatedItem.completedAt == "N/A"
            ? "N/A"
            : new Date(updatedItem.completedAt).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });
      }
      if (updatedItem.lastChangeAt) {
        updatedItem.lastChangeAt =
          updatedItem.lastChangeAt == "N/A"
            ? "N/A"
            : new Date(updatedItem.lastChangeAt).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              });
      }

      //Clio
      if (updatedItem.level4Currency) {
        updatedItem.level4Currency = `${
           isNaN(+updatedItem.level4Currency)
            ? "N/A"
            : new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(+updatedItem.level4Currency)
        }`;
      }
      if (
        (reportType == ReportTypes.SETTLEMENT_LIT ||
          reportType == ReportTypes.SETTLEMENT_PRELIT) &&
        updatedItem.totalClio
      ) {
        updatedItem.totalClio = `${
          updatedItem.totalClio == "N/A"
            ? "N/A"
            : new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(updatedItem.totalClio)
        }`;
      }

      if (!!updatedItem.total && !!updatedItem.totalClio) {
        updatedItem.totalHours = `${updatedItem.totalHours} || ${updatedItem.totalClioHours}`;
      }

      if (updatedItem.total === 0 && reportType === ReportTypes.LITIGATION_FUNNEL) {
        delete updatedItem.total
      }

      if (updatedItem.reportLevel2) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel2
        );
        delete updatedItem.reportLevel2;
      }
      if (updatedItem.reportLevel3) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel3
        );
        delete updatedItem.reportLevel3;
      }
      if (updatedItem.reportLevel4) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel4
        );
        delete updatedItem.reportLevel4;
      }
      if (updatedItem.reportLevel5) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel5
        );
        delete updatedItem.reportLevel5;
      }
      if (updatedItem.reportLevel6) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel6
        );
        delete updatedItem.reportLevel6;
      }

      return updatedItem;
    });
  };

  const loadData = () => {
    if (selectedReport === ReportTypes.EMPTY_MATTERS) {
      dispatch(startSpinner());
      ClioService.getApiClioEmptyMattersReport()
        .then((report) => {
          if (!!report) {
            setTableData(report);
            console.log(report);
          }
        })
        .catch(() =>
          MySwal.fire({
            icon: "error",
            title: (
              <p className="title">
                Error loading data. Please make sure you selected your user in
                Zoom Integration and try again in a couple minutes.
              </p>
            ),
          })
        )
        .finally(() => dispatch(stopSpinner()));
      return;
    }
    if (
      selectedReport === ReportTypes.OVERVIEW &&
      user?.roles?.filter((role) => userAdminRoles.includes(role)).length !==
        0 &&
      selectedUser === null
    ) {
      MySwal.fire({
        icon: "error",
        title: <p className="title">Please select staff</p>,
      });
      return;
    } else if (
      selectedReport === ReportTypes.OVERVIEW &&
      user?.roles?.filter((role) => userAdminRoles.includes(role)).length ===
        0 &&
      !phoneId
    ) {
      MySwal.fire({
        icon: "error",
        title: (
          <p className="title">Please set up Zoom before accessing reports</p>
        ),
      });
      return;
    }
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
      selectedStaff:
        selectedUser !== AllSelection
          ? usersList.find((user) => user.name === selectedUser)
          : null,
      endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
      litigationFilter: reportFilter
    } as ReportRequest;
    if (props.source === ReportSource.Leads) {
      LeadsService.postApiLeadsLeadsReport(reportRequest)
        .then((report) => {
          if (!!report) {
            setTableData(report.leadsByUser ?? report.leadsByDate ?? []);
          }
        })
        .catch(() =>
          MySwal.fire({
            icon: "error",
            title: <p className="title">Error loading data</p>,
          })
        )
        .finally(() => dispatch(stopSpinner()));
    } else {
      reportRequest.zoomId = phoneId;
      ClioService.postApiClioUserReport(reportRequest)
        .then((report) => {
          if (!!report) {
            setTableData(report);
            console.log(report);
          }
        })
        .catch((error) => {
          console.log(error);
          MySwal.fire({
            icon: "error",
            title: <p className="title">Error loading data</p>,
          });
        })
        .finally(() => dispatch(stopSpinner()));
    }
  };

  const handlePresetChange = (event) => {
    setSelectedPreset(event.target.value);
    const currentDate = dayjs();
    let selectedDate;
    let endDate = dayjs(new Date());

    switch (event.target.value) {
      case "LastMonth":
        selectedDate = currentDate.startOf("month").add(-1, "month");
        endDate = currentDate.startOf("month").add(-1, "day");
        break;
      case "Month":
        selectedDate = currentDate.startOf("month");
        break;
      case "Week":
        selectedDate = currentDate.startOf("week");
        break;
      case "Day":
        selectedDate = currentDate.startOf("day");
        break;
      default:
        selectedDate = null;
        break;
    }
    setStartDate(selectedDate);
    setEndDate(endDate);
  };

  const tableOptions: ReactTabulatorOptions = {
    dataTree: true,
    dataTreeFilter: true,
    height: 1000,
    dataTreeStartExpanded: selectedReport === ReportTypes.LITIGATION_FUNNEL
  };

  const DateRangeSelect = () => {
    return (
      <>
        <div className="col-3 mb-3">
          <DatePicker
            disableFuture={reportType !== ReportTypes.SOLES}
            label="Start date"
            className="me-3 w-100"
            value={startDate}
            onChange={(date) => { 
              console.log('start date: ', dayjs(date).format('YYYY-MM-DD'));
              setStartDate(date);
            }}
          />
        </div>
        <div className="col-3">
          <DatePicker
            disableFuture={reportType !== ReportTypes.SOLES && selectedReport !== ReportTypes.TASKS && selectedReport !== ReportTypes.LITIGATION_FUNNEL}
            label="End date"
            className="w-100"
            value={endDate}
            onChange={(date) => {
              console.log('end date: ', date);
              setEndDate(date);
            }}
          />
        </div>
        <div className="col-3">
          <FormControl className="w-100">
            <InputLabel>Select preset</InputLabel>
            <Select
              className="min-width-200"
              value={selectedPreset ?? "All time"}
              key={selectedPreset}
              label="Select preset"
              onChange={handlePresetChange}
            >
              <MenuItem key={0} value={AllSelection}>
                Select All
              </MenuItem>
              {Object.keys(Presets)
                .filter((key) => isNaN(Number(key)))
                .map((key) => {
                  return (
                    <MenuItem key={key} value={key}>
                      {HelpersService.getEnumKeyByEnumValue(
                        Presets,
                        Presets[key]
                      )}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </div>
      </>
    );
  };

  const AddSelect = (hideAll: boolean = false) => {
    return (
      <div className="col-3">
        <FormControl className="w-100">
          <InputLabel id="type-label" required>
            Team Member
          </InputLabel>
          <Select
            labelId="type-label"
            label="Select attorney*"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            {!hideAll && (
              <MenuItem key={0} value={AllSelection}>
                Select None
              </MenuItem>
            )}
            {usersList.map((user) => {
              return (
                <MenuItem key={user.id} value={user.name!}>
                  {user.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>
    );
  };

  const AddLitigationFilters = () => {
    return (<>
      {/* <h4>Select date range</h4> */}
      <div className="col-3 mb-3">
        <DatePicker
          disableFuture={reportType !== ReportTypes.SOLES}
          label="Task Due Date From"
          className="me-3 w-100"
          value={startDate}
          onChange={(date) => { 
            console.log('Task Due Date From: ', dayjs(date).format('YYYY-MM-DD'));
            setStartDate(date);
          }}
        />
      </div>
      <div className="col-3">
        <DatePicker
          disableFuture={reportType !== ReportTypes.SOLES && selectedReport !== ReportTypes.TASKS && selectedReport !== ReportTypes.LITIGATION_FUNNEL}
          label="Task Due Date To"
          className="w-100"
          value={endDate}
          onChange={(date) => {
            console.log('end date: ', date);
            setEndDate(date);
          }}
        />
      </div>
      <div className="w-100"></div>

      {/* <div className="col-3">
        <TextField
          fullWidth
          label="Matter"
          select
          value={reportFilter.matterId ?? ""}
          onChange={(e) =>
            setReportFilter((prev) => ({
              ...prev,
              matterId: e.target.value === "" ? undefined : Number(e.target.value),
            }))
          }
        >
          <MenuItem value="">All</MenuItem>
          {matterOptions.map((matter) => (
            <MenuItem key={matter.id} value={matter.id}>
              {matter.display_number}
            </MenuItem>
          ))}
        </TextField>
      </div> */}

      <div className="col-2">
        <TextField
          fullWidth
          label="Task Assignee"
          select
          value={selectedAssignee}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedAssignee(value);
            setReportFilter((prev) => ({
              ...prev,
              assignee: value !== AllSelection ? usersList.find((u) => u.name === value) ?? null : null,
            }));
          }}
        >
          <MenuItem value={AllSelection}>Select None</MenuItem>
          {usersList.map((u) => (
            <MenuItem key={u.id} value={u.name!}>{u.name}</MenuItem>
          ))}
        </TextField>
      </div>

      <div className="col-1">
        <TextField fullWidth label="AP" select value={reportFilter.ap ?? ""} onChange={(e) =>
          setReportFilter(prev => ({ ...prev, ap: e.target.value === "" ? undefined : Number(e.target.value) }))
        }>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="0">None</MenuItem>
          {assignedParalegals.map(a => <MenuItem key={a.id} value={a.id}>{a.option}</MenuItem>)}
        </TextField>
      </div>

      <div className="col-1">
        <TextField fullWidth label="RAA" select value={reportFilter.raa ?? ""} onChange={(e) =>
          setReportFilter(prev => ({ ...prev, raa: e.target.value === "" ? undefined : Number(e.target.value) }))
        }>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="0">None</MenuItem>
          {responsibleAttorneys.map(a => <MenuItem key={a.id} value={a.id}>{a.option}</MenuItem>)}
        </TextField>
      </div>

      <div className="col-1">
        <TextField fullWidth label="SMA" select value={reportFilter.sma ?? ""} onChange={(e) =>
          setReportFilter(prev => ({ ...prev, sma: e.target.value === "" ? undefined : Number(e.target.value) }))
        }>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="0">None</MenuItem>
          {seniorManagingAttorneys.map(a => <MenuItem key={a.id} value={a.id}>{a.option}</MenuItem>)}
        </TextField>
      </div>

      <div className="col-1">
        <TextField fullWidth label="SA" select value={reportFilter.sa ?? ""} onChange={(e) =>
          setReportFilter(prev => ({ ...prev, sa: e.target.value === "" ? undefined : Number(e.target.value) }))
        }>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="0">None</MenuItem>
          {supportingAttorneys.map(a => <MenuItem key={a.id} value={a.id}>{a.option}</MenuItem>)}
        </TextField>
      </div>

      <div className="col-3">
        <TextField
          fullWidth
          label="Key Word"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

    </>)
  }

  const GenerateTab = () => {
    if (!selectedReportData) {
      return;
    }
    return (
      <div className="row">
        <div className="row mt-3 pt-3 pb-3 gap-2">
          {!!selectedReportData.showDateRange && <DateRangeSelect />}
          {selectedReport === ReportTypes.OVERVIEW &&
            user?.roles?.filter((role) => userAdminRoles.includes(role))
              .length > 0 && <>{AddSelect(true)}</>}
          {(selectedReport === ReportTypes.TASKS ||
            selectedReport === ReportTypes.HOURS ||
            selectedReport === ReportTypes.SETTLEMENT_PRELIT ||
            selectedReport === ReportTypes.SETTLEMENT_LIT ||
            selectedReport === ReportTypes.LITIGATION_FUNNEL ||
            selectedReport === ReportTypes.NEGOTIATION_LOG ) &&
            user?.roles?.filter((role) => userAdminRoles.includes(role))
              .length > 0 && <>{AddSelect()}</>}
          {selectedReport === ReportTypes.LITIGATION_FUNNEL && user?.roles?.filter((role) => role === ApplicationRole.CLIO_STAFF)
              .length > 0 && <>{AddSelect()}</>}
          {selectedReport === ReportTypes.LITIGATION_FUNNEL && AddLitigationFilters()}
        </div>
        <div className="row ms-1 pb-3">
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className="submit-btn col-3"
            onClick={() => loadData()}
          >
            Load report
          </Button>
          {selectedReport === ReportTypes.TASKS &&
            user?.roles?.filter((role) => userAdminRoles.includes(role))
              .length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className="submit-btn col-3 ms-3"
                  onClick={DownloadActivitiesReport}
                >
                  Download Report
                </Button>
              </>
            )}
          {selectedReport === ReportTypes.LEADS_ATTORNEY &&
            user.roles.includes(ApplicationRole.ADMIN) && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className="submit-btn col-3 ms-3"
                  onClick={DownloadAttorneyLeadsReport}
                >
                  Download Report
                </Button>
              </>
            )}
          {selectedReport === ReportTypes.KPI &&
            user?.roles?.filter((role) => userAdminRoles.includes(role))
              .length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className="submit-btn col-3 ms-3"
                  onClick={DownloadKPIReport}
                >
                  Download Report
                </Button>
              </>
            )}
          {selectedReport === ReportTypes.EMPTY_MATTERS &&
            user?.roles?.filter((role) => userAdminRoles.includes(role))
              .length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className="submit-btn col-3 ms-3"
                  onClick={DownloadEmptyMatters}
                >
                  Download Report
                </Button>
              </>
            )}
          {selectedReport === ReportTypes.INACTIVE_MATTERS &&
            user?.roles?.filter((role) => userAdminRoles.includes(role))
              .length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className="submit-btn col-3 ms-3"
                  onClick={DownloadInactiveMatters}
                >
                  Download Report
                </Button>
              </>
            )}
            {selectedReport === ReportTypes.SOLES &&
            user?.roles?.filter((role) => userAdminRoles.includes(role))
              .length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className="submit-btn col-3 ms-3"
                  onClick={DownloadMontlySoles}
                >
                  Download Monthly Report
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className="submit-btn col-3 ms-3"
                  onClick={DownloadAttorneySoles}
                >
                  Download By Attorney Report
                </Button>
              </>
            )}
            {selectedReport === ReportTypes.HOURS &&
            user?.roles?.filter((role) => userAdminRoles.includes(role))
              .length > 0 && (
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className="submit-btn col-3 ms-3"
                  onClick={DownloadHourly}
                >
                  Download Report
                </Button>
            )}
            {selectedReport === ReportTypes.LITIGATION_FUNNEL &&
            user?.roles?.filter((role) => role === ApplicationRole.CLIO_STAFF || userAdminRoles.includes(role))
              .length > 0 && (
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  className="submit-btn col-3 ms-3"
                  onClick={DownloadLitigationReport}
                >
                  Download Report
                </Button>
            )}
            {selectedReport === ReportTypes.LITIGATION_FUNNEL && (
              <Button
                variant="contained"
                color="secondary"
                className="submit-btn col-3 ms-3"
                onClick={() => litigationTableRef.current?.saveAllNotes()}
                disabled={!notesDirty}
              >
                Save Notes
              </Button>
            )}
        </div>
        <div id="table-container">
            {selectedReport === ReportTypes.LITIGATION_FUNNEL ? (
              <LitigationReportTable
                ref={litigationTableRef}
                data={(filteredTableData as ReportModel[]) ?? []}
                matterOptions={matterOptions}
                onDirtyChange={setNotesDirty}
              />
            ) : (
              <ReactTabulator
                onRef={(ref) => (table = ref)}
                columns={selectedReportData.columns}
                data={updatePropertyNames(filteredTableData)}
                options={tableOptions}
                className={`css-class-${selectedReport}`}
              />
            )}
          </div>
      </div>
    );
  };

  const DownloadActivitiesReport = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      selectedStaff: usersList.find((user) => user.name === selectedUser)
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/downloadUsersTasksReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `UserTasks.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadAttorneyLeadsReport = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getAttorneyLeadsReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `RejectedLeadsByAttorney${startDate}--${endDate}.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };
  const DownloadKPIReport = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getKPIReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `KPI-report-${startDate}--${endDate}.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadEmptyMatters = () => {
    dispatch(startSpinner());
    fetch(`${OpenAPI.BASE}/api/File/getMattersWithOldChanges`, {
      method: "GET",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `EmptyMatters.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadInactiveMatters = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/donwloadInactiveMatters`, {
      method: "GET",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `InactiveMatters.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadMontlySoles = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getSolesMonthReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `SolesMontly.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadAttorneySoles = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getSolesAttyReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `SolesAttorney.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };
  const DownloadHourly = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
      selectedStaff: usersList.find((user) => user.name === selectedUser)
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getHoursReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `HoursReport.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadLitigationReport = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
      selectedStaff: usersList.find((user) => user.name === selectedUser),
      litigationFilter: reportFilter,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getLitigationFunnelReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `LitigationReport.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };
  

  if (!user && !phoneId) {
    return null;
  }
  const handleTabChange = (event, newValue: number) => {
    switch (newValue) {
      case 0: {
        setSelectedReport(ReportTypes.OVERVIEW);
        break;
      }
      case 1: {
        setSelectedReport(ReportTypes.TASKS);
        break;
      }
      case 2: {
        setSelectedPreset("Month");
        setStartDate(dayjs().startOf("month"));
        setEndDate(dayjs(new Date()));
        setSelectedReport(ReportTypes.HOURS);
        break;
      }
      case 3: {
        setSelectedReport(ReportTypes.EMPTY_MATTERS);
        break;
      }
      case 4: {
        setSelectedReport(ReportTypes.INACTIVE_MATTERS);
        break;
      }
    }
    setTab(newValue);
    setTableData([]);
  };

  return (
    <div className="reports-page">
      <h2 className="title">{selectedReportData?.header}</h2>
      <div>
        <pre>{selectedReportData?.description}</pre>
      </div>

      {reportType == "Overview" && (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Overview"
              {...TabsHelpers.parseProps(0)}
            />
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Task Activity"
              {...TabsHelpers.parseProps(1)}
            />
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Hours"
              {...TabsHelpers.parseProps(2)}
            />
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Empty matters"
              {...TabsHelpers.parseProps(3)}
            />
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Inactive matters"
              {...TabsHelpers.parseProps(4)}
            />
          </Tabs>
        </Box>
      )}
      {GenerateTab()}
    </div>
  );
}
